import { Box, CircularProgress, Grid, Typography } from '@material-ui/core';
import { useEntity } from '@backstage/plugin-catalog-react';
import { GroupEntity } from '@backstage/catalog-model';
import { InfoCard, Link } from '@backstage/core-components';
import { GroupSummary, useWoboContext } from '../../hooks/useWoboContext';

function GroupLink({ group }: { group: GroupSummary }) {
  if (!group.found) return <>{group.ref} (not found)</>;
  return <Link to={group.path}>{group.title}</Link>;
}

/**
 * Plain-text/link fallback for the worksOnBehalfOf relation, independent of
 * the catalog graph view — not every viewer opens the graph, so the target
 * needs to be visible directly on the entity overview page too. Shows
 * inherited WOBO (PAT-1066) and whether an own value overrides it.
 */
export function EntityWorksOnBehalfOfCard() {
  const { entity } = useEntity();
  const group = entity as GroupEntity;
  // Every level except the company root can hold or inherit a WOBO.
  const level = entity.metadata.annotations?.['team/level'] ?? 'team';
  const wobo = useWoboContext(group, undefined, level !== 'company');
  if (level === 'company') return null;

  const { own, target, inherited, loading, error } = wobo;
  const rawOwn = (entity.spec as { worksOnBehalfOf?: string } | undefined)
    ?.worksOnBehalfOf;

  if (own.kind === 'none' && (loading || !inherited)) return null;

  let main: JSX.Element;
  let caption: JSX.Element | undefined;

  if (own.kind === 'none') {
    main = <GroupLink group={target!} />;
    caption = (
      <>
        Inherited from <GroupLink group={inherited!.from} />
      </>
    );
  } else if (loading) {
    main = (
      <Box display="flex" justifyContent="center">
        <CircularProgress size={24} />
      </Box>
    );
  } else if (own.kind === 'invalid') {
    main = <Typography color="textSecondary">{rawOwn} (not found)</Typography>;
  } else if (error) {
    main = <Typography>{rawOwn}</Typography>;
  } else if (!target?.found) {
    main = <Typography color="textSecondary">{rawOwn} (not found)</Typography>;
  } else {
    main = <GroupLink group={target} />;
    if (inherited && inherited.target.ref !== own.targetRef) {
      caption = (
        <>
          Overrides inherited: <GroupLink group={inherited.from} /> →{' '}
          <GroupLink group={inherited.target} />
        </>
      );
    } else if (inherited) {
      caption = (
        <>
          Same as inherited from <GroupLink group={inherited.from} />
        </>
      );
    }
  }

  return (
    <Grid item xs={12} md={6}>
      <InfoCard title="Works On Behalf Of" variant="gridItem">
        {main}
        {caption && (
          <Typography variant="caption" color="textSecondary" component="div">
            {caption}
          </Typography>
        )}
      </InfoCard>
    </Grid>
  );
}
